import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { chapterApi } from '@/api/chapterApi'
import { ApiError, friendlyMessage } from '@/api/http'
import { multimodalApi } from '@/api/multimodalApi'
import { toast } from 'sonner'
import { readerApi, type ReaderExportFormat } from '@/api/readerApi'
import { ensureContentLanguageFont, injectFontLink } from '@/lib/contentFonts'
import { ChapterCanvas } from '@/components/reader/ChapterCanvas'
import { ChapterNav } from '@/components/reader/ChapterNav'
import { ExportCenter } from '@/components/reader/ExportCenter'
import { ReaderAudioBar } from '@/components/reader/ReaderAudioBar'
import { ReaderComicFlow } from '@/components/reader/ReaderComicFlow'
import { ReaderToolbar } from '@/components/reader/ReaderToolbar'
import {
  latestStoryboardAsset,
  normalizeReaderTab,
  ReaderMultimodalError,
  ReaderMultimodalLoading,
  ReaderTabBar,
  type ReaderTab,
} from '@/components/reader/ReaderMultimodalTabs'
import { ReaderVideoView } from '@/components/reader/ReaderVideoView'
import { TocDrawer } from '@/components/reader/TocDrawer'
import {
  loadReaderSettings,
  saveReaderSettings,
  type ReaderSettings,
} from '@/components/reader/readerSettings'
import { Button } from '@/components/ui/button'
import { downloadBlob } from '@/lib/download'
import { useUiStore } from '@/stores/uiStore'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (typeof window === 'undefined' ? false : window.innerWidth <= 768))

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return isMobile
}

function readingPositionKey(projectId: string, branchId: string, chapterNo: number) {
  return `nf.reader-position:${projectId}:${branchId}:${chapterNo}`
}

function isNotFound(error: unknown) {
  return error instanceof ApiError && error.status === 404
}

export default function ReaderPage() {
  const { id, n } = useParams()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const setCurrentBranchId = useUiStore((s) => s.setCurrentBranchId)
  const [tocOpen, setTocOpen] = useState(() => !n)
  const [settings, setSettings] = useState<ReaderSettings>(loadReaderSettings)
  const [readingProgress, setReadingProgress] = useState(0)
  const [exportingFormat, setExportingFormat] = useState<ReaderExportFormat | null>(null)
  const [exportCenterOpen, setExportCenterOpen] = useState(false)
  const locationTab = useMemo(() => normalizeReaderTab(`${location.search}${location.hash}`), [location.hash, location.search])
  const [activeTab, setActiveTab] = useState<ReaderTab>(locationTab)
  const restoreKeyRef = useRef<string | null>(null)

  const chapterNo = useMemo(() => {
    if (!n) return null
    const parsed = Number.parseInt(n, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [n])

  useEffect(() => {
    if (!n) setTocOpen(true)
  }, [n])

  useEffect(() => {
    saveReaderSettings(settings)
    if (settings.fontFamily === 'kai') {
      injectFontLink('/fonts/lxgw-wenkai-screen-webfont/lxgwwenkaigbscreen.css')
    } else {
      injectFontLink('/fonts/noto-serif-sc/400.css')
    }
  }, [settings])

  const tocQuery = useQuery({
    queryKey: ['reader', 'toc', id, currentBranchId],
    queryFn: () => readerApi.toc(id!, currentBranchId),
    enabled: Boolean(id),
  })

  const bundleQuery = useQuery({
    queryKey: ['reader', 'bundle', id, currentBranchId, chapterNo],
    queryFn: () => readerApi.bundle(id!, chapterNo!, currentBranchId),
    enabled: Boolean(id && chapterNo),
  })

  const bundle = bundleQuery.data
  const chapterId = bundle?.chapter.id ?? null

  // 按作品语言懒加载对应脚本字体 (日/韩/阿/泰), 自托管零外部请求。
  const contentLanguage = bundle?.project.content_language
  useEffect(() => {
    ensureContentLanguageFont(contentLanguage)
  }, [contentLanguage])
  const projectTitle = bundle?.project.title || tocQuery.data?.project.title || t('reader.title')
  const toolbarTitle = bundle?.chapter
    ? t('reader.chapterHeading', { number: bundle.chapter.chapter_no, title: bundle.chapter.title || t('reader.untitled') })
    : projectTitle

  useEffect(() => {
    setActiveTab(locationTab)
  }, [locationTab])

  useEffect(() => {
    const onHashChange = () => setActiveTab(normalizeReaderTab(`${window.location.search}${window.location.hash}`))
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const chapterAssetsQuery = useQuery({
    queryKey: ['reader', 'chapter-assets', chapterId],
    queryFn: () => chapterApi.assets(chapterId!),
    enabled: Boolean(chapterId && activeTab === 'comic'),
    retry: false,
  })

  const storyboardAsset = useMemo(
    () => latestStoryboardAsset(chapterId, chapterAssetsQuery.data ?? []),
    [chapterAssetsQuery.data, chapterId],
  )

  const panelsQuery = useQuery({
    queryKey: ['reader', 'storyboard-panels', storyboardAsset?.id],
    queryFn: () => multimodalApi.listStoryboardPanels(storyboardAsset!.id),
    enabled: Boolean(storyboardAsset?.id && activeTab === 'comic'),
    retry: false,
  })

  useEffect(() => {
    if (!id || !chapterNo) return
    const key = readingPositionKey(id, currentBranchId, chapterNo)
    if (restoreKeyRef.current === key || bundleQuery.isLoading) return
    restoreKeyRef.current = key
    const saved = Number.parseInt(localStorage.getItem(key) ?? '0', 10)
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: Number.isFinite(saved) ? saved : 0 })
    })
  }, [bundleQuery.isLoading, chapterNo, currentBranchId, id])

  useEffect(() => {
    if (!id || !chapterNo) return undefined
    const key = readingPositionKey(id, currentBranchId, chapterNo)
    const onScroll = () => {
      const scrollTop = window.scrollY
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
      const progress = Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100))
      setReadingProgress(progress)
      localStorage.setItem(key, String(Math.round(scrollTop)))
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [chapterNo, currentBranchId, id])

  const goToChapter = useCallback((target: number | null | undefined) => {
    if (!id || !target) return
    navigate(`/projects/${encodeURIComponent(id)}/reader/ch/${target}#${activeTab}`)
  }, [activeTab, id, navigate])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return
      if (event.key === 'ArrowLeft') goToChapter(bundle?.nav.prev_chapter_no)
      if (event.key === 'ArrowRight') goToChapter(bundle?.nav.next_chapter_no)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [bundle?.nav.next_chapter_no, bundle?.nav.prev_chapter_no, goToChapter])

  const handleExport = async (format: ReaderExportFormat) => {
    if (!id || exportingFormat) return
    setExportingFormat(format)
    try {
      const file = await readerApi.exportProject(id, format, currentBranchId)
      downloadBlob(file.blob, file.filename)
      toast.success(t('reader.exportDone'))
    } catch (error) {
      toast.error(friendlyMessage(error, t('reader.exportFailed')))
    } finally {
      setExportingFormat(null)
    }
  }

  if (!id) return null

  const loadError = tocQuery.error || bundleQuery.error
  const currentPath = `${location.pathname}${location.search}${location.hash}`

  const renderActiveTab = () => {
    if (activeTab === 'text') {
      return (
        <ChapterCanvas
          bundle={bundle}
          isLoading={tocQuery.isLoading || (Boolean(chapterNo) && bundleQuery.isLoading)}
          settings={settings}
          isMobile={isMobile}
        />
      )
    }

    if (tocQuery.isLoading || (Boolean(chapterNo) && bundleQuery.isLoading)) return <ReaderMultimodalLoading />

    if (activeTab === 'video') {
      return (
        <ReaderVideoView
          chapterId={chapterId}
          posterUrl={bundle?.assets.header_image?.url}
          hasVideo={Boolean(bundle?.modalities.video || bundle?.assets.video?.length)}
        />
      )
    }

    if (chapterAssetsQuery.isLoading || panelsQuery.isLoading) return <ReaderMultimodalLoading />
    if (chapterAssetsQuery.error && !isNotFound(chapterAssetsQuery.error)) {
      return <ReaderMultimodalError message={friendlyMessage(chapterAssetsQuery.error, t('common.serviceError'))} />
    }
    if (panelsQuery.error && !isNotFound(panelsQuery.error)) {
      return <ReaderMultimodalError message={friendlyMessage(panelsQuery.error, t('common.serviceError'))} />
    }
    return <ReaderComicFlow panels={panelsQuery.data ?? []} />
  }

  const showSecondaryAudio = activeTab !== 'text' && Boolean(bundle?.assets.audio?.url)

  return (
    <section className="min-h-[calc(100vh-3.5rem)] bg-background">
      <ReaderToolbar
        title={toolbarTitle}
        branch={bundle?.branch ?? tocQuery.data?.branch}
        settings={settings}
        exportingFormat={exportingFormat}
        onOpenToc={() => setTocOpen(true)}
        onSettingsChange={setSettings}
        onBranchChange={(branchId) => {
          setCurrentBranchId(branchId)
          navigate(`/projects/${encodeURIComponent(id)}/reader`)
        }}
        onOpenExportCenter={() => setExportCenterOpen(true)}
      />

      <ExportCenter
        open={exportCenterOpen}
        exportingFormat={exportingFormat}
        onOpenChange={setExportCenterOpen}
        onExport={(format) => void handleExport(format)}
      />

      <TocDrawer
        open={tocOpen}
        onOpenChange={setTocOpen}
        projectId={id}
        toc={tocQuery.data}
        currentChapterNo={chapterNo}
        readingProgress={readingProgress}
        isMobile={isMobile}
      />

      {loadError ? (
        <div className="grid min-h-[calc(100vh-7rem)] place-items-center px-4 py-12">
          <div className="max-w-md rounded-lg border bg-card p-6 text-center">
            <AlertCircle className="mx-auto mb-3 size-6 text-destructive" />
            <p className="font-semibold text-foreground">{t('reader.loadFailed')}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {friendlyMessage(loadError, t('common.serviceError'))}
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <Button asChild variant="outline">
                <Link to={`/projects/${encodeURIComponent(id)}`}>
                  <ArrowLeft className="size-4" />
                  {t('reader.backToProject')}
                </Link>
              </Button>
              <Button onClick={() => {
                void tocQuery.refetch()
                void bundleQuery.refetch()
              }}
              >
                {t('common.retry')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ReaderTabBar
            activeTab={activeTab}
            currentPath={currentPath}
            onTabChange={setActiveTab}
          />
          {renderActiveTab()}
          {showSecondaryAudio ? (
            <div className={isMobile ? 'pb-24' : 'mx-auto w-full max-w-[760px] px-4 pb-8'}>
              <ReaderAudioBar audioUrl={bundle!.assets.audio!.url} isMobile={isMobile} />
            </div>
          ) : null}
          {bundle ? <ChapterNav projectId={id} bundle={bundle} currentTab={activeTab} onOpenToc={() => setTocOpen(true)} /> : null}
        </>
      )}
    </section>
  )
}
